import * as React from "react";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "../../hooks/use_backend";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useParams } from "react-router-dom";
import { commandUpdateSiteConfig } from "../../scripts/api_v2/commands_client";
import Snackbar from "../../scripts/snackbar";
import { StatusIcon } from "../general/status";
import { Status as StatusEnum } from "../../model/box_status";
import { getPeakShavingStatus } from "../installation/peak_shaving_utils";
import { enqueueSnackbar } from "notistack";
import Loader from "../general/loader";
import { REACT_QUERY_KEYS } from "../../enums/react_query_key_enum";
import { fetchSiteProduct } from "../../scripts/api_v2/site_client";
import { useSiteData } from "hooks/use_site";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import AlertIcon from "../alerting/alert_icon";
import { AlertIcons, AlertLevelClass } from "components/alerting/alert_enums";
import { CollapsableCard } from "components/general/collapsable_card";
import BoxAlerts from "components/alerting/box_alerts";
import EastIcon from "@mui/icons-material/East";
import { fmtAsKw } from "scripts/utils";

function getDisplayOptimizer(optimizer) {
  const val = optimizer?.toLowerCase();
  if (val === "elion" || val === "elion-enris") return "Imbalance";
  if (val === "self-cons") return "Self consumption";
  return optimizer;
}

export function ProductCard({ tooltip, required_privilege, ...props }) {
  const params = useParams();
  const [editing, setEditing] = React.useState(false);
  const [controlEdits, setControlEdits] = React.useState({});
  const [controlEditState, setControlEditState] = React.useState({});
  const backend = useBackend();
  const { data: siteData, error, isLoading, isRefetching, refetch } = useSiteData();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();

  const {
    data: product,
    isError,
    isLoading: isProductLoading,
    refetch: productRefetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PRODUCT, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProduct(backend.fetch, params.site_id),
    refetchInterval: 10000,
  });

  const { mutate: controlMutation, isPending: isMutating } = useMutation({
    mutationFn: async ({ controlEdits, mutatedField, mutatedValue, isString }) => {
      return await commandUpdateSiteConfig(backend.fetch, params.site_id, controlEdits);
    },
    onSuccess: async (data, variables, context) => {
      await refetch();
      setEditing(false);
    },
    onError: (err, variables, context) => {
      Snackbar.error("Could not save config: " + err);
    },
  });

  function handleEditControl() {
    if (siteData?.config) {
      setControlEditState(siteData.config);
      setEditing(true);
    } else {
      enqueueSnackbar("No config data available", { variant: "error" });
    }
  }

  function handleCancelConfig() {
    setControlEdits({});
    setControlEditState({});
    setEditing(false);
  }

  function getTitle(alert, level, icon) {
    if (alert) {
      return (
        <span style={{ display: "flex", alignItems: "center" }}>
          Product info
          <AlertIcon level={level * 10} icon={icon} style={{ marginLeft: "6px" }} />
          <b style={{ color: "#aaa", marginLeft: "6px" }}>{product.act_product}</b>
        </span>
      );
    }
    return (
      <span style={{ display: "flex", alignItems: "center" }}>
        Product info
        <b style={{ color: "#aaa", marginLeft: "6px" }}>{product.act_product}</b>
      </span>
    );
  }

  async function commitControl() {
    if (Object.values(controlEdits).length === 0) {
      handleCancelConfig();
    } else {
      const mutationData = {
        commit: true,
        controlEdits: controlEdits,
        mutatedField: null,
        mutatedValue: null,
        isString: false,
      };
      controlMutation(mutationData);
    }
  }

  if (!siteData || !product) return;

  const alert = !product.status_ok || product.conf_override;
  const level = !product.status_ok ? AlertLevelClass.ERROR : AlertLevelClass.WARNING;
  const icon = !product.status_ok ? AlertIcons.ERROR : AlertIcons.WARNING;

  const status = siteData ? siteData["status"] : null;

  if (isLoading || isProductLoading || isMutating || isRefetching) {
    return (
      <CollapsableCard
        sx={{ ...props.sx, width: "100%", minHeight: 240, height: "100%" }}
        title={getTitle(alert, level, icon)}
      >
        <Loader fill={true} />
      </CollapsableCard>
    );
  }

  return (
    <CollapsableCard tooltip={tooltip} sx={props.sx} title={getTitle(alert, level, icon)}>
      <Stack direction="column">
        <ProductStatusLine product_data={product} alert={alert} />
        <ProductLine
          name="OPTIMIZER"
          value={getDisplayOptimizer(product.conf_optimizer)}
          override={product.conf_optimizer !== product.act_optimizer}
          overrideValue={getDisplayOptimizer(product.act_optimizer)}
        />
        <ProductLine
          name="MAX INJECTION SETPOINT"
          value={fmtAsKw(product.conf_max_grid_inject)}
          override={product.conf_max_grid_inject !== product.act_max_grid_inject}
          overrideValue={fmtAsKw(product.act_max_grid_inject)}
        />
        <ProductLine
          name="MAX OFFTAKE SETPOINT"
          value={fmtAsKw(product.conf_max_grid_offtake)}
          override={product.conf_max_grid_offtake !== product.act_max_grid_offtake}
          overrideValue={fmtAsKw(product.act_max_grid_offtake)}
        />
        <BoxStatusLine status={status} peakShavingStatus={getPeakShavingStatus(siteData)} />
      </Stack>
    </CollapsableCard>
  );
}

function getBackgroundColor(product_data) {
  if (!product_data.status_ok) return "#fa467030";
  else if (product_data.conf_override) return "#fcb44730";
  return "White";
}

function ProductStatusLine({ product_data, alert }) {
  const theme = useTheme();
  if (!alert)
    return (
      <Box
        backgroundColor={getBackgroundColor(product_data)}
        display="flex"
        alignItems="center"
        pl={2}
        mt={-1}
        borderRadius={2} // rounded corners
        sx={{
          maxWidth: "100%",
        }}
      >
        <Stack direction="row" alignItems="center">
          <BoxAlerts sx={{}} hideOK alerts={[]} />
          <Typography variant="h7" sx={{ display: "block", pl: 2 }}>
            {
              <b style={{ color: theme.palette.text.primary_bold, display: "inline-block" }}>
                {product_data.status_txt_ext}
              </b>
            }
          </Typography>
        </Stack>
      </Box>
    );
  const level = !product_data.status_ok ? AlertLevelClass.ERROR : AlertLevelClass.WARNING;
  const icon = !product_data.status_ok ? AlertIcons.ERROR : AlertIcons.WARNING;
  return (
    <Box
      backgroundColor={getBackgroundColor(product_data)}
      display="flex"
      alignItems="center"
      p={2}
      mt={-1}
      mb={1}
      borderRadius={2} // rounded corners
      sx={{ maxWidth: "100%" }}
    >
      <Stack direction="row" alignItems="center">
        <AlertIcon level={level * 10} icon={icon} />
        <Typography variant="h7" sx={{ display: "block", pl: 2 }}>
          {
            <b style={{ color: theme.palette.text.primary_bold, display: "inline-block" }}>
              {product_data.status_txt_ext}
            </b>
          }
        </Typography>
      </Stack>
    </Box>
  );
}

const ProductName = ({ name }) => {
  const theme = useTheme();
  return (
    <Typography
      variant="h7"
      sx={{
        display: "inline-block",
        pl: 1,
        color: theme.palette.text.primary_bold,
        width: "240px",
        textAlign: "left",
        fontWeight: "bold",
      }}
    >
      {name.toUpperCase()}
    </Typography>
  );
};

const ProductValue = ({ value, override = false }) => {
  return (
    <Typography
      variant="h7"
      sx={{
        display: "inline-block",
        ml: 5,
        textDecoration: override ? "line-through" : null,
        color: "#999",
        width: "120px",
        textAlign: "left",
        fontWeight: "bold",
      }}
    >
      {value}
    </Typography>
  );
};

function ProductLine({ name, value, override, overrideValue }) {
  return (
    <Stack direction="row" alignItems="center" pl={2} pt={1}>
      <ProductName name={name} />
      <ProductValue value={value} override={override} />
      {override && (
        <>
          <EastIcon />
          <ProductValue value={overrideValue} />
        </>
      )}
    </Stack>
  );
}

function getPeakShavingChipStatus({ hasEligibleBattery }) {
  return hasEligibleBattery ? StatusEnum.ONLINE : StatusEnum.OFFLINE;
}

function BoxStatusLine({ status, peakShavingStatus }) {
  const theme = useTheme();
  return (
    <Stack direction="row" alignItems="center" pl={2} pt={1}>
      <ProductName name={"CONTROL STATUS"} />
      <Box sx={{ display: "flex", gap: 0.5, ml: 4 }}>
        <StatusIcon label="Control loop" status={status.control_loop} />
        <StatusIcon label="MQTT" status={status.mqtt} />
        <StatusIcon label="Modbus" status={status.modbus} />
        <StatusIcon label="Schematic" status={status.schematic} />
        {peakShavingStatus.enabled && (
          <StatusIcon label="Peak shaving" status={getPeakShavingChipStatus(peakShavingStatus)} />
        )}
      </Box>
    </Stack>
  );
}
